import { Novu } from '@novu/js';
import { markRaw } from 'vue';
import { Api } from '@/utils/api';

export interface NovuSubscriber {
	subscriberId: string;
	subscriberHash: string | null;
	/**
	 * Organization the subscriber's Inbox is scoped to (the Novu `organization`
	 * context), derived backend-side from the user's market. `null` when the
	 * market has no context (e.g. izify) — the Inbox is then unscoped.
	 */
	organization: string | null;
	/**
	 * HMAC of the `{ organization: { id } }` context, signed with the Novu API
	 * key. Must accompany the context whenever HMAC is enforced, or Novu rejects
	 * the session. `null` whenever `organization` is `null`.
	 */
	contextHash: string | null;
}

/** Shared Novu connection settings, named to match the `MzNotificationCenter` props. */
export interface NovuConfig {
	applicationIdentifier: string;
	apiUrl: string;
	socketUrl: string;
}

/**
 * Resolve the Novu connection settings, or `null` when no application identifier
 * is configured (the feature is then effectively off).
 */
export function getNovuConfig(): NovuConfig | null {
	const applicationIdentifier = import.meta.env.VITE_NOVU_APPLICATION_IDENTIFIER;
	if (!applicationIdentifier) {
		return null;
	}

	return {
		applicationIdentifier,
		apiUrl: import.meta.env.VITE_NOVU_BACKEND_URL || 'https://eu.api.novu.co',
		socketUrl: import.meta.env.VITE_NOVU_SOCKET_URL || 'https://eu.ws.novu.co',
	};
}

/**
 * The authenticated subscriber identity for the browser Novu client. The id is
 * the bridge-prefixed `uzzer:{id}`; the hashes are server-computed HMACs that
 * authenticate the in-app Inbox / preferences calls (see uzzer-backend
 * `GET /notifications/subscriber`). Everything must come from the backend —
 * never build the subscriberId, the organization, or sign anything client-side.
 */
export async function fetchNovuSubscriber(): Promise<NovuSubscriber> {
	const response = await Api.get('/notifications/subscriber');
	const body = await response.json();

	return {
		subscriberId: body.data.subscriber_id,
		subscriberHash: body.data.subscriber_hash ?? null,
		organization: body.data.organization ?? null,
		contextHash: body.data.context_hash ?? null,
	};
}

/**
 * The Novu `context` + matching `contextHash` to send, or an empty object when
 * the subscriber has no organization context. Novu rejects a session that sends
 * a `context` without its `contextHash`, so the two are only ever sent together.
 */
function contextOptions(subscriber: NovuSubscriber): { context: { organization: { id: string } }; contextHash: string } | object {
	if (!subscriber.organization || !subscriber.contextHash) {
		return {};
	}

	return {
		context: { organization: { id: subscriber.organization } },
		contextHash: subscriber.contextHash,
	};
}

/**
 * Build an authenticated Novu client for the current user, or `null` when no
 * application identifier is configured (the feature is then effectively off).
 */
export async function initNovuClient(): Promise<Novu | null> {
	const config = getNovuConfig();
	if (!config) {
		console.warn('VITE_NOVU_APPLICATION_IDENTIFIER is not set');
		return null;
	}

	const subscriber = await fetchNovuSubscriber();

	return markRaw(
		new Novu({
			applicationIdentifier: config.applicationIdentifier,
			subscriber: subscriber.subscriberId,
			// HMAC is enforced in every environment, so the hash is normally always present.
			// It's omitted only as defensive degradation when the backend couldn't sign
			// (bridge down / no NOVU_API_KEY); the Inbox then connects unauthenticated.
			...(subscriber.subscriberHash ? { subscriberHash: subscriber.subscriberHash } : {}),
			apiUrl: config.apiUrl,
			socketUrl: config.socketUrl,
			...contextOptions(subscriber),
		}),
	);
}
